/**
 * Sub-Customers Redux Sagas
 */

import { all, call, delay, extractErrorMessage, i18n, put, select, showSuccessToast, takeLatest } from '@spcc/core'
import type { PayloadAction } from '@spcc/core'
import { subCustomersApi } from '../infrastructure/api/sub-customers-api'
import { subCustomersActions as A, type SubCustomerSaveRequest } from './sub-customers.slice'
import { selectSubCustomerForm, selectSubCustomersFilter } from './sub-customers.selectors'
import type { PaginatedSubCustomers, SubCustomerFilter } from '../domain/types'
import { SUB_CUSTOMER_SEARCH_DEBOUNCE_MS, T_KEYS } from '../domain/constants'

const translate = (key: string) => i18n.t(key) as string

/** One request for the current page; takeLatest drops a stale response when filters change mid-flight. */
function* fetchSaga(action: PayloadAction<unknown>) {
  if (action.type === A.setSearch.type) yield delay(SUB_CUSTOMER_SEARCH_DEBOUNCE_MS)
  try {
    const filter: SubCustomerFilter = yield select(selectSubCustomersFilter)
    const result: PaginatedSubCustomers = yield call(subCustomersApi.getSubCustomers, { ...filter, search: filter.search?.trim() })
    yield put(A.fetchSuccess({ items: result.data, pagination: result.pagination }))
  } catch (error) {
    yield put(A.fetchFailure(extractErrorMessage(error, translate(T_KEYS.SUB_CUSTOMERS.LOAD_ERROR))))
  }
}

function* saveSaga(action: PayloadAction<SubCustomerSaveRequest>) {
  const { name, phone, customerType } = action.payload
  const form: ReturnType<typeof selectSubCustomerForm> = yield select(selectSubCustomerForm)
  const filter: SubCustomerFilter = yield select(selectSubCustomersFilter)
  try {
    if (form.editing) {
      yield call(subCustomersApi.updateSubCustomer, form.editing.documentId, { name, phone: phone || null, customerType })
    } else {
      // Admins create under the customer picked in the header (the button is disabled without one).
      const tenant = filter.tenantDocumentId ? { tenant: filter.tenantDocumentId } : {}
      yield call(subCustomersApi.createSubCustomer, { name, phone: phone || undefined, customerType, ...tenant })
    }
    yield put(A.saveSuccess())
    yield call(showSuccessToast, translate(T_KEYS.SUB_CUSTOMERS.SAVE_SUCCESS))
    // Re-read the page so ordering, totals and page count stay right.
    yield put(A.fetchSubCustomers())
  } catch (error) {
    yield put(A.saveFailure(extractErrorMessage(error, translate(T_KEYS.SUB_CUSTOMERS.SAVE_ERROR))))
  }
}

export function* subCustomersSaga() {
  yield all([
    takeLatest(
      [A.init.type, A.fetchSubCustomers.type, A.setSearch.type, A.setCustomerType.type, A.setPage.type, A.setPageSize.type],
      fetchSaga,
    ),
    takeLatest(A.saveSubCustomer.type, saveSaga),
  ])
}
